import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../../ui/dialog";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../ui/select";
import { Switch } from "../../../ui/switch";
import { CateringMenuCategory, CateringCategoryGroup } from "../../../../hooks/useCateringMenus";

interface EditCategoryDialogProps {
  category: CateringMenuCategory | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (categroy: any, type: string, updateIndex?: string) => void;
  availableGroups?: CateringCategoryGroup[];
}

export function EditCategoryDialog({ category, open, onOpenChange, onSave, availableGroups = [] }: EditCategoryDialogProps) {
  const isCreate = !category?.id;
  const [name, setName] = useState("");
  const [selectionType, setSelectionType] = useState<"all_included" | "choice">("all_included");
  const [minSelection, setMinSelection] = useState<number | null>(null);
  const [maxSelection, setMaxSelection] = useState<number | null>(null);
  const [isRequired, setIsRequired] = useState(false);
  const [groupId, setGroupId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (category) {
      setName(category.name);
      setSelectionType(category.selection_type);
      setMinSelection(category.min_selection);
      setMaxSelection(category.max_selection);
      setIsRequired(category.is_required);
      setGroupId(category.group_id || null);
    } else {
      setName("");
      setSelectionType("all_included");
      setMinSelection(null);
      setMaxSelection(null);
      setIsRequired(false);
      setGroupId(null);
    }
  }, [category, open]);

  const handleSave = async () => {
    /*  setSaving(true); */
    const payload = {
      name,
      selection_type: selectionType,
      min_selection: selectionType === "choice" ? minSelection : null,
      max_selection: selectionType === "choice" ? maxSelection : null,
      is_required: selectionType === "choice" ? isRequired : false,
      group_id: groupId || null,
    };
    if (isCreate) {
      onSave(payload, "add");
    } else {
      onSave(payload, "update", category.id);
    }
  };
  console.log(availableGroups, 'availableGroupsavailableGroups')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isCreate ? "Add Category" : "Edit Category"}</DialogTitle>
          <DialogDescription>{isCreate ? "Create a new category for this menu." : "Update category settings and selection rules."}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Category Name</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Premium Mains" />
          </div>

          {/* Parent Group assignment */}
          {availableGroups.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="parentGroup">Parent Group</Label>
              <Select value={groupId || "__none__"} onValueChange={(v) => setGroupId(v === "__none__" ? null : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="None (ungrouped)" />
                </SelectTrigger>
                <SelectContent style={{ zIndex: "99" }}>
                  <SelectItem value="__none__">None (ungrouped)</SelectItem>
                  {availableGroups.map((g) => (
                    <SelectItem key={g.id} value={g.id}>
                      {g.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Assign this category under a parent group for nested display.</p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="selectionType">Selection Type</Label>
            <Select value={selectionType} onValueChange={(v) => setSelectionType(v as "all_included" | "choice")}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent style={{ zIndex: "99" }}>
                <SelectItem value="all_included">All Included</SelectItem>
                <SelectItem value="choice">Choice</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {selectionType === "choice" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="minSelection">Min Selection</Label>
                  <Input id="minSelection" type="number" min={0} value={minSelection ?? ""} onChange={(e) => setMinSelection(e.target.value ? parseInt(e.target.value) : null)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maxSelection">Max Selection</Label>
                  <Input id="maxSelection" type="number" min={1} value={maxSelection ?? ""} onChange={(e) => setMaxSelection(e.target.value ? parseInt(e.target.value) : null)} />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="isRequired">Required</Label>
                <Switch id="isRequired" checked={isRequired} onCheckedChange={setIsRequired} />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={saving || !name.trim()}>
            {saving ? "Saving..." : isCreate ? "Create Category" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
